/**
 * Flow Transformations
 * Converts between visual React Flow state and database OnboardingFlow schema
 */

/**
 * Transform visual state (React Flow nodes/edges) to database schema
 * @param {Array} nodes - React Flow nodes
 * @param {Array} edges - React Flow edges
 * @returns {Object} - Database-compatible flow structure
 */
export const visualStateToDatabase = (nodes, edges) => {
  // Extract stages from nodes and sort by stageNumber
  const stages = nodes
    .map(node => node.data.stage)
    .sort((a, b) => a.stageNumber - b.stageNumber);

  return { stages };
};

/**
 * Transform database OnboardingFlow to visual React Flow state
 * @param {Object} flow - OnboardingFlow document
 * @returns {Object} - { nodes, edges } for React Flow
 */
export const databaseToVisualState = (flow) => {
  if (!flow || !flow.stages) {
    return { nodes: [], edges: [] };
  }

  // Create nodes from stages
  const nodes = flow.stages.map((stage, index) => ({
    id: `stage-${stage.stageNumber}`,
    type: 'stageNode',
    position: {
      x: index * 320,
      y: 100
    },
    data: {
      stage: {
        ...stage,
        personaId: stage.personaId?._id || stage.personaId,
        availableTools: (stage.availableTools || []).map(t => ({
          toolId: t.toolId?._id || t.toolId,
          enabled: t.enabled !== false,
          customConfig: t.customConfig || {}
        }))
      }
    }
  }));

  // Create edges based on stage sequence
  const edges = flow.stages.slice(0, -1).map((stage, index) => ({
    id: `edge-${stage.stageNumber}-${flow.stages[index + 1].stageNumber}`,
    source: `stage-${stage.stageNumber}`,
    target: `stage-${flow.stages[index + 1].stageNumber}`,
    type: 'smoothstep',
    animated: true,
    label: stage.transitionTrigger?.type?.replace(/_/g, ' ') || 'auto'
  }));

  return { nodes, edges };
};

/**
 * Validate flow structure for errors
 * @param {Object} flow - OnboardingFlow document
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validateFlowStructure = (flow) => {
  const errors = [];

  if (!flow.stages || flow.stages.length === 0) {
    errors.push('At least one stage is required');
    return { isValid: false, errors };
  }

  // Check stage number sequence
  const stageNumbers = flow.stages.map(s => s.stageNumber).sort((a, b) => a - b);
  for (let i = 0; i < stageNumbers.length; i++) {
    if (stageNumbers[i] !== i + 1) {
      errors.push(`Stage numbers must be sequential starting from 1. Found gap at ${i + 1}`);
      break;
    }
  }

  // Check for duplicate stage numbers
  const duplicates = stageNumbers.filter((num, idx) => stageNumbers.indexOf(num) !== idx);
  if (duplicates.length > 0) {
    errors.push(`Duplicate stage numbers found: ${duplicates.join(', ')}`);
  }

  // Check each stage has required fields
  flow.stages.forEach((stage, index) => {
    const stageNum = stage.stageNumber || index + 1;

    if (!stage.stageName || stage.stageName.trim() === '') {
      errors.push(`Stage ${stageNum}: Name is required`);
    }

    if (!stage.personaId) {
      errors.push(`Stage ${stageNum}: Persona must be selected`);
    }

    if (!stage.transitionTrigger || !stage.transitionTrigger.type) {
      errors.push(`Stage ${stageNum}: Transition trigger must be configured`);
    }

    // Validate transition trigger specific requirements
    if (stage.transitionTrigger?.type === 'tool_success' && !stage.transitionTrigger.toolName) {
      errors.push(`Stage ${stageNum}: Tool name required for tool_success transition`);
    }

    if (stage.transitionTrigger?.type === 'time_based' && !stage.transitionTrigger.delayMinutes) {
      errors.push(`Stage ${stageNum}: Delay minutes required for time_based transition`);
    }

    // Validate tools
    if (stage.availableTools && stage.availableTools.length > 0) {
      const toolIds = stage.availableTools.map(t => t.toolId).filter(id => id);
      const uniqueToolIds = new Set(toolIds);
      if (toolIds.length !== uniqueToolIds.size) {
        errors.push(`Stage ${stageNum}: Duplicate tools detected`);
      }

      stage.availableTools.forEach((tool, toolIdx) => {
        if (!tool.toolId) {
          errors.push(`Stage ${stageNum}, Tool ${toolIdx + 1}: Tool ID is required`);
        }
      });
    }

    // Validate assessments
    if (stage.requiredAssessments && stage.requiredAssessments.length > 0) {
      stage.requiredAssessments.forEach((assessment, assessmentIdx) => {
        if (!assessment.assessmentKey) {
          errors.push(`Stage ${stageNum}, Assessment ${assessmentIdx + 1}: Assessment key is required`);
        }

        if (assessment.passingCriteria) {
          const { minScore, maxScore } = assessment.passingCriteria;
          if (minScore !== undefined && maxScore !== undefined && minScore > maxScore) {
            errors.push(`Stage ${stageNum}, Assessment ${assessmentIdx + 1}: minScore cannot be greater than maxScore`);
          }
        }
      });
    }
  });

  // Validate completion config
  if (!flow.completionConfig) {
    errors.push('Completion configuration is required');
  } else {
    const { dynamicAssignment, therapistPersonaId, triageStrategyId } = flow.completionConfig;

    if (dynamicAssignment?.enabled) {
      if (!triageStrategyId) {
        // Warning, not error - can use platform default
        // errors.push('Triage strategy must be selected when dynamic assignment is enabled');
      }
      if (!dynamicAssignment.fallbackPersona) {
        errors.push('Fallback persona is required when dynamic assignment is enabled');
      }
    } else {
      if (!therapistPersonaId) {
        errors.push('Therapist persona is required for completion (or enable dynamic assignment)');
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculate flow statistics
 * @param {Object} flow - OnboardingFlow document with stats
 * @returns {Object} - Computed statistics
 */
export const calculateFlowStats = (flow) => {
  const { stats } = flow;

  if (!stats) {
    return {
      completionRate: 0,
      dropOffRate: 0,
      averageDuration: 0
    };
  }

  const completionRate = stats.totalStarted > 0
    ? ((stats.totalCompleted / stats.totalStarted) * 100).toFixed(1)
    : 0;

  const dropOffRate = stats.totalStarted > 0
    ? (((stats.totalStarted - stats.totalCompleted) / stats.totalStarted) * 100).toFixed(1)
    : 0;

  return {
    completionRate: `${completionRate}%`,
    dropOffRate: `${dropOffRate}%`,
    averageDuration: stats.averageDurationMinutes
      ? `${stats.averageDurationMinutes.toFixed(1)} min`
      : 'N/A'
  };
};

/**
 * Generate a new stage template
 * @param {number} stageNumber - Stage number
 * @returns {Object} - New stage object
 */
export const createNewStage = (stageNumber) => ({
  stageNumber,
  stageName: `Stage ${stageNumber}`,
  personaId: null,
  availableTools: [],
  requiredAssessments: [],
  transitionTrigger: {
    type: 'tool_success',
    toolName: ''
  },
  greetingMessage: '',
  transitionMessage: ''
});

/**
 * Renumber stages after deletion/reordering
 * @param {Array} stages - Array of stage objects
 * @returns {Array} - Stages with corrected stage numbers
 */
export const renumberStages = (stages) => {
  return stages.map((stage, index) => ({
    ...stage,
    stageNumber: index + 1
  }));
};
